import { DropdownSelectItemType } from '@dixa/lib-shared-habla';
import type {
  SearchFilterCondition,
  SearchFilterValueString,
} from 'lib-search-types';
import { isDateFilterCondition } from 'lib-search-ui';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';

import { ConditionFieldProps } from '../../../conditionField';

type UseConditionFieldSelectOptions<
  TCondition extends SearchFilterCondition<'conversations'>,
> = {
  condition: TCondition;
  onChange: ConditionFieldProps<TCondition>['onChange'];
};

type UseConditionFieldSelectResult = {
  handleClear: VoidFunction;
  handleChange: (item: DropdownSelectItemType) => void;
  mapValuesToItems: () => DropdownSelectItemType[];
};

export function useConditionFieldSelect<
  TCondition extends SearchFilterCondition<'conversations'>,
>({
  condition,
  onChange,
}: UseConditionFieldSelectOptions<TCondition>): UseConditionFieldSelectResult {
  const trackValue = useMemo(
    () =>
      isDateFilterCondition(condition) ||
      ['initial_direction', 'initial_channel', 'status', 'duration'].includes(
        condition.field,
      ),
    [condition],
  );

  const handleClear = useCallback(() => {
    ampli.searchFilterValueCleared({
      type: condition.field,
      count: condition.values.length,
    });

    onChange([]);
  }, [condition.field, condition.values.length, onChange]);

  const handleChange = useCallback(
    (item: DropdownSelectItemType) => {
      const { name: label, id: value } = item;
      const nextValues = [...condition.values] as SearchFilterValueString[];
      const isNew = !condition.values.some(({ value }) => value === item.id);

      if (isNew) {
        const nextValue: SearchFilterValueString = {
          label: label as string,
          value: value.toString(),
        };
        nextValues.push(nextValue);

        ampli.searchFilterValueAdded({
          type: condition.field,
          operator: condition.operator,
          value: trackValue ? nextValue.value : undefined,
        });
      } else {
        nextValues.splice(
          nextValues.findIndex((i) => i.value === value),
          1,
        );
      }

      onChange(nextValues);
    },
    [condition, trackValue, onChange],
  );

  const mapValuesToItems = useCallback(() => {
    return (condition.values as SearchFilterValueString[]).map(
      ({ label, value }) => ({
        id: value,
        name: label || value,
      }),
    );
  }, [condition]);

  return { handleChange, handleClear, mapValuesToItems };
}
